// src/pages/offlineSales/components/OfflineOrderA4Invoice.jsx
import React from 'react';
import { useLocalization } from '../../../contexts/LocalizationContext';
import { getImageUrl } from '../../../utils/imageUtils';
import A4DocumentLayout from '../../../components/common/A4DocumentLayout/A4DocumentLayout';

const OfflineOrderA4Invoice = ({ order }) => {
  const { t, formatCurrency } = useLocalization();

  if (!order) return null;

  const totalsRows = [];
  if (parseFloat(order.totalDiscount) > 0) totalsRows.push({ label: t('offlineSales.itemDiscount'), value: `-${formatCurrency(order.totalDiscount)}` });
  if (parseFloat(order.orderDiscount) > 0) totalsRows.push({ label: t('offlineSales.orderDiscount'), value: `-${formatCurrency(order.orderDiscount)}` });
  if (parseFloat(order.loyaltyDiscountAmount) > 0) totalsRows.push({ label: t('offlineSales.loyaltyDiscount'), value: `-${formatCurrency(order.loyaltyDiscountAmount)}` });
  if (parseFloat(order.totalTax) > 0) totalsRows.push({ label: t('offlineSales.tax'), value: `+${formatCurrency(order.totalTax)}` });
  if (parseFloat(order.deliveryCharge) > 0) totalsRows.push({ label: t('offlineSales.deliveryCharge'), value: `+${formatCurrency(order.deliveryCharge)}` });
  totalsRows.push({ label: t('offlineSales.grandTotal'), value: formatCurrency(order.grandTotal), variant: 'grand' });
  totalsRows.push({ label: t('offlineSales.totalPaid'), value: formatCurrency(order.paidAmount) });
  if (parseFloat(order.balanceAmount) > 0) {
    totalsRows.push({ label: t('offlineSales.balanceDue'), value: formatCurrency(order.balanceAmount), color: 'var(--color-error, #ef4444)' });
  }

  return (
    <A4DocumentLayout
      title={order.Shop?.name || 'STORE INVOICE'}
      logoUrl={getImageUrl(order.Shop?.logo)}
      subtitle={t('offlineSales.salesReceiptInvoice', 'Offline Sales Invoice')}
      documentNumber={`#${order.orderNumber}`}
      metaRows={[
        { label: t('offlineSales.date'), value: new Date(order.createdAt).toLocaleString() },
        { label: t('offlineSales.statusCol'), value: (order.OrderStatus?.label || order.orderStatusKey || '').toUpperCase() },
      ]}
      infoCards={[
        {
          title: t('offlineSales.customerInformation'),
          primary: order.customer?.name || order.customerName || t('offlineSales.cashSales'),
          lines: [
            order.customer?.phone && `${t('offlineSales.customerPhone')}: ${order.customer.phone}`,
            order.customer?.address && `${t('offlineSales.customerAddress')}: ${order.customer.address}`,
            order.customer?.email && `${t('offlineSales.customerEmail')}: ${order.customer.email}`,
            order.customer?.tinNumber && `${t('offlineSales.tinNumber')}: ${order.customer.tinNumber}`,
            order.customer?.vatNumber && `${t('offlineSales.vatNumber')}: ${order.customer.vatNumber}`,
          ].filter(Boolean),
        },
        {
          title: t('offlineSales.orderSummary'),
          lines: [
            { label: t('offlineSales.cashier'), value: order.cashier?.name || '—', bold: true },
            { label: t('offlineSales.fulfillmentMethod'), value: (order.fulfillmentMethod || 'counter_pickup').replace('_', ' ').toUpperCase(), bold: true },
            { label: t('offlineSales.paymentStatusCol'), value: (order.paymentStatus || 'unpaid').toUpperCase(), bold: true },
          ],
        },
      ]}
      items={{
        columns: [
          { header: '#', align: 'center', width: '40px' },
          { header: t('offlineSales.items') },
          { header: t('offlineSales.unitPrice'), align: 'right' },
          { header: t('offlineSales.qty'), align: 'center' },
          { header: t('offlineSales.lineDiscount'), align: 'right' },
          { header: t('offlineSales.lineTotal'), align: 'right' },
        ],
        rows: (order.orderItems || []).map((item, index) => [
          index + 1,
          <>
            <strong>{item.Product?.name || item.name}</strong>
            {item.ProductVariation?.sku && <span style={{ opacity: 0.7, fontSize: '0.8em' }}> — SKU: {item.ProductVariation.sku}</span>}
            {item.freeQuantity > 0 && (
              <div style={{ fontSize: '0.8em', fontWeight: 600 }}>{t('offlineSales.itemsIncludedFree', { count: item.freeQuantity })}</div>
            )}
          </>,
          formatCurrency(item.unitPrice),
          item.quantity,
          formatCurrency(item.lineDiscount || 0),
          <strong>{formatCurrency(item.lineTotal || item.quantity * item.unitPrice)}</strong>,
        ]),
      }}
      summary={{
        notes: { label: t('offlineSales.notes'), text: order.specialNotes || order.notes || t('common.none', 'None') },
        totals: totalsRows,
      }}
      footerText={t('offlineSales.thankYouMessage', 'Thank you for your business!')}
      footerNote="This is a computer-generated invoice."
    />
  );
};

export default OfflineOrderA4Invoice;
